import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { useTranslation } from "react-i18next";
import PrivacyFooter from "../footer/PrivacyFooter";
import PrivacyHeader from "../header/PrivacyHeader";
import { Seo } from "../../shared/seo/Seo";
import { LegalJsonLd } from "../../shared/seo/JsonLd";
import type { RoutePath } from "../../shared/config/seo";

type DocSection = { title: string; body: string[] };
type RequisiteRow = { label: string; value: string };

interface LegalDocumentProps {
  ns: "offer" | "privacy";
  path: RoutePath;
}

// Split a leading "01. " ordinal so the number can be rendered as a monospace
// accent and tracked in the table of contents.
const splitOrdinal = (title: string): { num: string | null; label: string } => {
  const match = title.match(/^(\d+)\.\s*(.*)$/);
  return match ? { num: match[1], label: match[2] } : { num: null, label: title };
};

const LegalDocument = ({ ns, path }: LegalDocumentProps) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "ru" ? "ru" : "uz";

  const preamble = t(`${ns}.preamble`, { returnObjects: true }) as string[];
  const sections = t(`${ns}.sections`, { returnObjects: true }) as DocSection[];
  const requisiteRows = t(`${ns}.requisites.rows`, { returnObjects: true }) as RequisiteRow[];

  // Reading-progress bar (decorative; degrades to zero-width without JS).
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

  // Active-section tracking for the sticky table of contents.
  const [active, setActive] = useState(0);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          const idx = Number((visible[0].target as HTMLElement).dataset.index);
          if (!Number.isNaN(idx)) setActive(idx);
        }
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    sectionRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [sections.length]);

  return (
    <div className="min-h-[100dvh] bg-paper text-ink">
      <Seo path={path} lang={lang} />
      <LegalJsonLd lang={lang} path={path} />

      {/* Reading progress — fixed, single emerald accent, no glow. */}
      <motion.div
        aria-hidden
        style={{ scaleX: progress }}
        className="fixed inset-x-0 top-0 z-50 h-[3px] origin-left bg-emerald"
      />

      <PrivacyHeader />

      <div className="mx-auto max-w-container px-4 py-10 sm:px-6 lg:py-16">
        <div className="lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-14">
          {/* Sticky table of contents (desktop only). */}
          <aside className="hidden lg:block">
            <nav aria-label={lang === "ru" ? "Содержание" : "Mundarija"} className="sticky top-24">
              <p className="mb-4 text-xs uppercase tracking-widest text-ink-3">
                {lang === "ru" ? "Разделы" : "Bo'limlar"}
              </p>
              <ul className="space-y-1 border-l border-line">
                {sections.map((section, i) => {
                  const { num, label } = splitOrdinal(section.title);
                  const isActive = active === i;
                  return (
                    <li key={i}>
                      <a
                        href={`#sec-${i}`}
                        aria-current={isActive ? "true" : undefined}
                        className={`-ml-px flex gap-2 border-l-2 py-1.5 pl-4 text-sm transition-colors duration-200 ease-out ${
                          isActive
                            ? "border-emerald font-semibold text-ink"
                            : "border-transparent text-ink-3 hover:text-ink-2"
                        }`}
                      >
                        {num && <span className="text-xs text-emerald-ink">{num}</span>}
                        <span className="leading-snug">{label}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          {/* Document body. */}
          <main className="min-w-0 max-w-[68ch]">
            <header className="mb-10">
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center rounded-full bg-emerald-tint px-3 py-1 text-sm font-semibold text-emerald-ink">
                  {t(`${ns}.status_label`)}
                </span>
                <span className="text-xs uppercase tracking-wider text-ink-3">
                  {t(`${ns}.doc_badge`)}
                </span>
              </div>
              <h1 className="font-display text-3xl font-extrabold leading-tight tracking-tight md:text-4xl">
                {t(`${ns}.title`)}
              </h1>
              <p className="mt-3 text-lg leading-relaxed text-ink-2">{t(`${ns}.subtitle`)}</p>
            </header>

            <dl className="mb-10 grid gap-6 rounded-4xl border border-line bg-surface p-6 sm:grid-cols-2 sm:p-8">
              <div>
                <dt className="text-xs uppercase tracking-widest text-ink-3">{t(`${ns}.meta.date_label`)}</dt>
                <dd className="mt-1 font-medium">{t(`${ns}.meta.date`)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest text-ink-3">{t(`${ns}.meta.executor_label`)}</dt>
                <dd className="mt-1 font-medium">{t(`${ns}.meta.executor`)}</dd>
                <dd className="mt-1 text-sm text-ink-3">{t(`${ns}.meta.reg`)}</dd>
              </div>
            </dl>

            <p className="mb-8 border-l-2 border-emerald pl-5 text-lg font-medium leading-relaxed text-ink">
              {t(`${ns}.lead`)}
            </p>

            <div className="space-y-4 leading-relaxed text-ink-2">
              {preamble.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            {sections.map((section, i) => {
              const { num, label } = splitOrdinal(section.title);
              return (
                <section
                  key={i}
                  id={`sec-${i}`}
                  data-index={i}
                  ref={(el) => {
                    sectionRefs.current[i] = el;
                  }}
                  className="mt-12 scroll-mt-28"
                >
                  <h2 className="mb-4 flex items-baseline gap-3 font-display text-xl font-bold tracking-tight md:text-2xl">
                    {num && <span className="text-base text-emerald-ink">{num}</span>}
                    <span>{label}</span>
                  </h2>
                  <div className="space-y-4 leading-relaxed text-ink-2">
                    {section.body.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                </section>
              );
            })}

            <section className="mt-12">
              <h2 className="mb-4 font-display text-xl font-bold tracking-tight md:text-2xl">{t(`${ns}.requisites.title`)}</h2>
              <dl className="overflow-hidden rounded-4xl border border-line bg-surface">
                {requisiteRows.map((row, i) => (
                  <div
                    key={i}
                    className="flex flex-col gap-1 border-b border-line p-5 last:border-b-0 sm:flex-row sm:gap-6 sm:px-8"
                  >
                    <dt className="text-xs uppercase tracking-widest text-ink-3 sm:w-56 sm:shrink-0 sm:pt-0.5">{row.label}</dt>
                    <dd className="font-medium">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <p className="mt-12 rounded-4xl bg-emerald-tint p-6 font-medium leading-relaxed text-emerald-hover sm:p-8">
              {t(`${ns}.acceptance`)}
            </p>
          </main>
        </div>
      </div>

      <PrivacyFooter />
    </div>
  );
};

export default LegalDocument;
